import { Component, Inject, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { SpinnerService } from 'src/app/core/services/spinner.service';
import { ToasterService } from 'src/app/core/services/toastr.service';
import { TeamsService } from 'src/app/core/services/teams.service';
import { GeoFenceService } from 'src/app/core/services/geo-fence.service';
import { geofenceData, geofenceListing } from 'src/app/models/geofenceData';
import { driverData } from 'src/app/models/driverData';
import { CommonResponse } from 'src/app/models/commomResponse';
import { driverService } from 'src/app/core/services/driver.service';
import { numberConst } from 'src/app/core/const/number.const';
import { teamListData, teamListResponse } from 'src/app/models/teamListData';

interface TeamData {
  _id: string;
  teamName: string;
}

@Component({
  selector: 'megastore-admin-driver-approval',
  templateUrl: './driver-approval.component.html',
  styleUrls: ['./driver-approval.component.scss']
})
export class DriverApprovalComponent implements OnDestroy {
  approvalForm: FormGroup;
  submitted = false;
  destroy$: Subject<boolean> = new Subject<boolean>();
  geofenceList: Array<geofenceData> = [];
  teamList: Array<TeamData> = [];
  driverInfo: driverData | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { driver: driverData },
    private dialog: MatDialogRef<DriverApprovalComponent>,
    private formBuilder: FormBuilder,
    private _toasterService: ToasterService,
    private _teamsService: TeamsService,
    private _geoFenceService: GeoFenceService,
    private _driverService: driverService,
    private _spinnerService: SpinnerService,
    private _translateService: TranslateService
  ) {
    this.approvalForm = this.formBuilder.group({
      geofenceId: ['', [Validators.required]],
      teamId: ['', [Validators.required]]
    });

    this.driverInfo = this.data.driver;
    this.getGeofenceData();
  }

  getGeofenceData(): void {
    this._spinnerService.addToLoader('getGeofenceData');
    this._geoFenceService
      .getGeofenceListing('')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: geofenceListing) => {
          this._spinnerService.removeFromLoader('getGeofenceData');
          this.geofenceList = res.doc;
        },
        error: () => {
          this._spinnerService.removeFromLoader('getGeofenceData');
        }
      });
  }

  onGeofenceSelect(geofenceId: string): void {
    this._spinnerService.addToLoader('getTeams');
    // We need to get teams associated with the selected geofence
    this._teamsService
      .getTeamList(`geofenceId=${geofenceId}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: teamListData) => {
          this._spinnerService.removeFromLoader('getTeams');
          if (res && res.teams) {
            this.teamList = res.teams.map((team: teamListResponse) => ({
              _id: team._id,
              teamName: team.teamName
            }));
          } else {
            this.teamList = [];
          }
          // Clear team selection when geofence changes
          this.approvalForm.get('teamId')?.setValue('');
        },
        error: () => {
          this._spinnerService.removeFromLoader('getTeams');
          this.teamList = [];
        }
      });
  }

  onSubmit(): void {
    this.submitted = true;
    if (this.approvalForm.valid && this.driverInfo) {
      this._spinnerService.addToLoader('approveDriver');
      
      // First, update the driver with geofence assignment
      const geofenceId = this.approvalForm.get('geofenceId')?.value;
      const teamId = this.approvalForm.get('teamId')?.value;
      
      // Create the update data object with geofence, team, and approved status
      const updateData: { 
        geofenceId: string; 
        teamId: string; 
        isApproovedByAdmin: number; 
      } = {
        geofenceId: geofenceId,
        teamId: teamId,
        isApproovedByAdmin: parseInt(numberConst.TWO)
      };
      console.log(updateData);
      this._driverService
        .updateDriver(updateData, this.driverInfo._id || '')
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: CommonResponse) => {
            this._spinnerService.removeFromLoader('approveDriver');
            this._toasterService.successToastr(
              this._translateService.instant('DriverApproved')
            );
            
            // Find the selected geofence to include in the response
            const selectedGeofence = this.geofenceList.find(gf => gf._id === geofenceId);
            const selectedTeam = this.teamList.find(team => team._id === teamId);
            
            // Add geofence and team data to the response
            if (selectedGeofence || selectedTeam) {
              res.data = {
                ...res.data,
                geofence: selectedGeofence ? {
                  _id: selectedGeofence._id,
                  name: selectedGeofence.name,
                  geoFence: selectedGeofence.geoFence
                } : undefined,
                geofenceId: geofenceId,
                team: selectedTeam ? {
                  teamName: selectedTeam.teamName
                } : undefined
              };
            }
            
            this.dialog.close(res);
          },
          error: () => {
            this._spinnerService.removeFromLoader('approveDriver');
          }
        });
    }
  }

  onCancel(): void {
    this.dialog.close();
  }

  ngOnDestroy(): void {
    this.destroy$.complete();
    this.destroy$.unsubscribe();
  }
} 